import ApiClient from './ApiClient';
import getWootAxios from 'dashboard/helper/getWootAxios';

class AiCloserAPI extends ApiClient {
  constructor() {
    super('ailab/assistants', { accountScoped: true });
  }

  // Config APIs
  getConfig(assistantId) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/config`);
  }

  updateConfig(assistantId, data) {
    return getWootAxios().patch(`${this.url}/${assistantId}/ai_closer/config`, {
      config: data,
    });
  }

  previewPrompt(assistantId, query, { inboxId = null } = {}) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/preview_prompt`,
      { query, inbox_id: inboxId }
    );
  }

  // Config versions (P1 governance)
  getConfigVersions(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/config/versions`
    );
  }

  getReleaseReadiness(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/config/release_readiness`
    );
  }

  getSavedTestSets(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets`
    );
  }

  createSavedTestSet(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets`,
      {
        saved_test_set: data,
      }
    );
  }

  updateSavedTestSet(assistantId, savedTestSetId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets/${savedTestSetId}`,
      {
        saved_test_set: data,
      }
    );
  }

  deleteSavedTestSet(assistantId, savedTestSetId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets/${savedTestSetId}`
    );
  }

  exportSavedTestSets(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets/export_library`
    );
  }

  importSavedTestSets(assistantId, savedTestSets) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/saved_test_sets/import_library`,
      {
        saved_test_sets: savedTestSets,
      }
    );
  }

  getReleaseSnapshots(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/release_snapshots`
    );
  }

  createReleaseSnapshot(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/release_snapshots`,
      {
        release_snapshot: data,
      }
    );
  }

  updateReleaseSnapshot(assistantId, releaseSnapshotId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/release_snapshots/${releaseSnapshotId}`,
      {
        release_snapshot: data,
      }
    );
  }

  deleteReleaseSnapshot(assistantId, releaseSnapshotId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/release_snapshots/${releaseSnapshotId}`
    );
  }

  getBatchBaselines(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/batch_baselines`
    );
  }

  createBatchBaseline(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/batch_baselines`,
      {
        batch_baseline: data,
      }
    );
  }

  updateBatchBaseline(assistantId, batchBaselineId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/batch_baselines/${batchBaselineId}`,
      {
        batch_baseline: data,
      }
    );
  }

  deleteBatchBaseline(assistantId, batchBaselineId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/batch_baselines/${batchBaselineId}`
    );
  }

  rollbackConfig(assistantId, versionId, releaseEvidence = {}) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/rollback`,
      {
        version_id: versionId,
        ...releaseEvidence,
      }
    );
  }

  publishConfig(
    assistantId,
    {
      versionId,
      scope = 'all',
      inboxIds = [],
      trafficPercentage = 100,
      releaseEvidence = {},
      note = '',
    }
  ) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/publish`,
      {
        version_id: versionId,
        scope,
        inbox_ids: inboxIds,
        traffic_percentage: trafficPercentage,
        note,
        ...releaseEvidence,
      }
    );
  }

  clearRollouts(
    assistantId,
    { inboxIds = [], releaseEvidence = {}, note = '' } = {}
  ) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/clear_rollouts`,
      {
        inbox_ids: inboxIds,
        note,
        ...releaseEvidence,
      }
    );
  }

  setConfigVersionState(assistantId, { versionId, state }) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/set_version_state`,
      {
        version_id: versionId,
        state,
      }
    );
  }

  simulateConfig(
    assistantId,
    {
      query,
      inboxId = null,
      includePrompt = true,
      includeResponse = false,
      estimatedCompletionTokens = 250,
      model = null,
      stream = false,
    } = {}
  ) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/simulate`,
      {
        query,
        inbox_id: inboxId,
        include_prompt: includePrompt,
        include_response: includeResponse,
        estimated_completion_tokens: estimatedCompletionTokens,
        model,
        stream,
      }
    );
  }

  batchSimulateConfig(
    assistantId,
    {
      cases = [],
      includePrompt = false,
      includeResponse = true,
      estimatedCompletionTokens = 250,
      model = null,
    } = {}
  ) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/config/batch_simulate`,
      {
        cases,
        include_prompt: includePrompt,
        include_response: includeResponse,
        estimated_completion_tokens: estimatedCompletionTokens,
        model,
      }
    );
  }

  getAbReport(assistantId, { days = 7, versionIds = [] } = {}) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/config/ab_report`,
      {
        params: { days, version_ids: versionIds },
      }
    );
  }

  // Product APIs (backward compatible - single product)
  getProduct(assistantId) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/product`);
  }

  updateProduct(assistantId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/product`,
      {
        product: data,
      }
    );
  }

  // Products APIs (multiple products)
  getProducts(assistantId) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/products`);
  }

  createProduct(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/products`,
      {
        product: data,
      }
    );
  }

  getProductById(assistantId, productId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/products/${productId}`
    );
  }

  updateProductById(assistantId, productId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/products/${productId}`,
      { product: data }
    );
  }

  deleteProduct(assistantId, productId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/products/${productId}`
    );
  }

  // Answer Templates APIs
  getAnswerTemplates(assistantId, params = {}) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/answer_templates`,
      {
        params,
      }
    );
  }

  createAnswerTemplate(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates`,
      {
        answer_template: data,
      }
    );
  }

  updateAnswerTemplate(assistantId, templateId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/answer_templates/${templateId}`,
      { answer_template: data }
    );
  }

  deleteAnswerTemplate(assistantId, templateId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/answer_templates/${templateId}`
    );
  }

  importAnswerTemplatesCSV(assistantId, file) {
    const formData = new FormData();
    formData.append('file', file);
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates/import_csv`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  importAnswerTemplatesEvalCasesCSV(assistantId, file, options = {}) {
    const formData = new FormData();
    formData.append('file', file);
    Object.entries(options || {}).forEach(([k, v]) => {
      if (v === undefined || v === null || v === '') return;
      formData.append(k, v);
    });
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates/import_eval_cases_csv`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
  }

  exportAnswerTemplatesCSV(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/answer_templates/export_csv`,
      { responseType: 'blob' }
    );
  }

  autoTagAnswerTemplates(assistantId, options = {}) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates/auto_tag`,
      options || {}
    );
  }

  migrateAnswerTemplatesToArticles(
    assistantId,
    { portalId, disableTemplates = true } = {}
  ) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates/migrate_to_articles`,
      {
        portal_id: portalId,
        disable_templates: disableTemplates,
      }
    );
  }

  disableShadowedAnswerTemplates(assistantId) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/answer_templates/disable_shadowed`
    );
  }

  // Prices APIs
  getPrices(assistantId, params = {}) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/prices`, {
      params,
    });
  }

  createPrice(assistantId, data) {
    return getWootAxios().post(`${this.url}/${assistantId}/ai_closer/prices`, {
      price: data,
    });
  }

  updatePrice(assistantId, priceId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/prices/${priceId}`,
      {
        price: data,
      }
    );
  }

  deletePrice(assistantId, priceId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/prices/${priceId}`
    );
  }

  // Media APIs
  getMedia(assistantId, params = {}) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/media`, {
      params,
    });
  }

  createMedia(assistantId, data) {
    return getWootAxios().post(`${this.url}/${assistantId}/ai_closer/media`, {
      media: data,
    });
  }

  updateMedia(assistantId, mediaId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/media/${mediaId}`,
      {
        media: data,
      }
    );
  }

  deleteMedia(assistantId, mediaId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/media/${mediaId}`
    );
  }

  // Links APIs (AI gamer)
  getLinks(assistantId, params = {}) {
    return getWootAxios().get(`${this.url}/${assistantId}/ai_closer/links`, {
      params,
    });
  }

  createLink(assistantId, data) {
    return getWootAxios().post(`${this.url}/${assistantId}/ai_closer/links`, {
      link: data,
    });
  }

  updateLink(assistantId, linkId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/links/${linkId}`,
      {
        link: data,
      }
    );
  }

  deleteLink(assistantId, linkId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/links/${linkId}`
    );
  }

  // Testimonials APIs
  getTestimonials(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/testimonials`
    );
  }

  createTestimonial(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/testimonials`,
      {
        testimonial: data,
      }
    );
  }

  updateTestimonial(assistantId, testimonialId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/testimonials/${testimonialId}`,
      { testimonial: data }
    );
  }

  deleteTestimonial(assistantId, testimonialId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/testimonials/${testimonialId}`
    );
  }

  // Certificates APIs
  getCertificates(assistantId) {
    return getWootAxios().get(
      `${this.url}/${assistantId}/ai_closer/certificates`
    );
  }

  createCertificate(assistantId, data) {
    return getWootAxios().post(
      `${this.url}/${assistantId}/ai_closer/certificates`,
      {
        certificate: data,
      }
    );
  }

  updateCertificate(assistantId, certificateId, data) {
    return getWootAxios().patch(
      `${this.url}/${assistantId}/ai_closer/certificates/${certificateId}`,
      { certificate: data }
    );
  }

  deleteCertificate(assistantId, certificateId) {
    return getWootAxios().delete(
      `${this.url}/${assistantId}/ai_closer/certificates/${certificateId}`
    );
  }
}

export default new AiCloserAPI();
